import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { formatEuDate, formatEuDateTime } from '../utils/dateFormat';
import './DonateCodeGenerator.css';

const defaultDonateSummary = {
  totalPlayersWithCoins: 0,
  totalCoins: 0,
  averageCoins: 0,
  totalCodes: 0,
  totalCodeValue: 0,
  topCoinHolder: null
};

const defaultTracking = {
  windowHours: 72,
  totals: {
    got: 0,
    used: 0,
    moved: 0,
    net: 0,
    operations: 0,
    activeCitizens: 0
  },
  players: [],
  events: [],
  dailyData: []
};

function DonateCodeGenerator() {
  const { hasPermission, apiFetch } = useAuth();
  const { language } = useI18n();
  const canViewOverview = hasPermission('donate.view');
  const canGenerateCodes = hasPermission('codes.generate');
  const canManageCodes = hasPermission('codes.manage');
  const canManageCoins = hasPermission('coins.manage');
  const [amount, setAmount] = useState('');
  const [generatedCode, setGeneratedCode] = useState(null);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingCodes, setLoadingCodes] = useState(true);
  const [loadingTracking, setLoadingTracking] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [overview, setOverview] = useState({
    summary: defaultDonateSummary,
    coins: []
  });
  const [codes, setCodes] = useState([]);
  const [coinView, setCoinView] = useState('top10');
  const [search, setSearch] = useState('');
  const [deletingCodeId, setDeletingCodeId] = useState(null);
  const [updatingCitizenId, setUpdatingCitizenId] = useState(null);
  const [coinInputValues, setCoinInputValues] = useState({});
  const [manualCitizenId, setManualCitizenId] = useState('');
  const [manualAmount, setManualAmount] = useState('');
  const [tracking, setTracking] = useState(defaultTracking);
  const [trackingWindow, setTrackingWindow] = useState('72');

  const t = language === 'da'
    ? {
        failedOverview: 'Kunne ikke indlæse donationsoversigt',
        failedCodes: 'Kunne ikke indlæse aktive koder',
        failedGenerate: 'Kunne ikke generere kode',
        generatedSuccess: 'Donationskode genereret.',
        confirmDelete: 'Slet denne kode? Dette kan ikke fortrydes.',
        failedDelete: 'Kunne ikke slette kode',
        deletedSuccess: 'Kode slettet.',
        citizenRequired: 'Citizen ID er påkrævet.',
        validAmount: 'Indtast et gyldigt beloeb for denne handling.',
        failedUpdateCoins: 'Kunne ikke opdatere coins',
        coinsUpdated: 'Coins opdateret ({action}) for {id}.',
        copied: 'Kode kopieret til udklipsholder!',
        accessDenied: 'Adgang afvist:',
        needDonateView: 'Du skal have',
        toViewPage: 'for at se denne side.',
        title: 'Donationssystem overblik',
        subtitle: 'Overblik over coins, kodevaerdi og hurtig kodegenerering',
        refreshing: 'Opdaterer...',
        refreshOverview: 'Opdater overblik',
        refreshingCodes: 'Opdaterer koder...',
        refreshCodes: 'Opdater koder',
        amount: 'Beloeb ($)',
        enterAmount: 'Indtast beloeb',
        generating: 'Genererer...',
        generateCode: 'Generer kode',
        readOnly: 'Kun visning:',
        needGenerate: 'Du kan se data, men skal have',
        toCreate: 'for at oprette koder.',
        error: 'Fejl:',
        success: 'Succes:',
        codeGenerated: 'Kode genereret',
        copy: 'Kopi',
        instructions: 'Instruktioner',
        instructionText: 'Del koden med spilleren. De kan indløse den in-game via donationssystemet.',
        playersWithCoins: 'Spillere med coins',
        totalCoins: 'Samlede coins',
        averageCoins: 'Gennemsnitlige coins',
        generatedCodes: 'Genererede koder',
        totalCodeValue: 'Samlet kodevaerdi',
        topCoinHolder: 'Top coin-indehaver',
        na: 'N/A',
        coinBalances: 'Coin saldi',
        top10: 'Top 10',
        top50: 'Top 50',
        top100: 'Top 100',
        searchCoinBalances: 'søg efter citizenid eller spillernavn',
        loadingCoinBalances: 'Indlæser coin saldi...',
        noCoinBalances: 'Ingen coin-saldi fundet',
        playerName: 'Spillernavn',
        citizenId: 'Citizen ID',
        coins: 'Coins',
        manage: 'Administrer',
        unknown: 'Ukendt',
        manualCoinManagement: 'Manuel coin administration',
        amountShort: 'Beloeb',
        add: 'tilføj',
        remove: 'Fjern',
        set: 'sæt',
        activeCodes: 'Aktive koder',
        loadingActiveCodes: 'Indlæser aktive koder...',
        noActiveCodes: 'Ingen aktive koder fundet',
        actions: 'Handlinger',
        deleting: 'Sletter...',
        delete: 'Slet',
        trackingTitle: 'Donationssporing',
        trackingSubtitle: 'Hvor meget spillere fik, brugte og flyttede i perioden.',
        refreshTracking: 'Opdater sporing',
        loadingTracking: 'Indlæser donationssporing...',
        noTracking: 'Ingen donationsaktivitet fundet i perioden',
        last72h: 'Sidste 72 timer',
        lastWeek: 'Sidste 7 dage',
        allTime: 'Altid',
        graphTitle: 'Top spillere — Coin aktivitet',
        dailyActivity: 'Daglig aktivitet',
        got: 'Fik',
        used: 'Brugte',
        moved: 'Flyttet',
        net: 'Netto',
        operations: 'Operationer',
        activePlayers: 'Aktive spillere',
        lastAction: 'Seneste handling'
      }
    : {
        failedOverview: 'Failed to load donate overview',
        failedCodes: 'Failed to load active codes',
        failedGenerate: 'Failed to generate code',
        generatedSuccess: 'Donate code generated successfully.',
        confirmDelete: 'Delete this code? This cannot be undone.',
        failedDelete: 'Failed to delete code',
        deletedSuccess: 'Code deleted successfully.',
        citizenRequired: 'Citizen ID is required.',
        validAmount: 'Enter a valid amount for this action.',
        failedUpdateCoins: 'Failed to update coins',
        coinsUpdated: 'Coins updated ({action}) for {id}.',
        copied: 'Code copied to clipboard!',
        accessDenied: 'Access denied:',
        needDonateView: 'You need',
        toViewPage: 'to view this page.',
        title: 'Donate System Overview',
        subtitle: 'Overview of coins, code values, and quick code generation',
        refreshing: 'Refreshing...',
        refreshOverview: 'Refresh Overview',
        refreshingCodes: 'Refreshing Codes...',
        refreshCodes: 'Refresh Codes',
        amount: 'Amount ($)',
        enterAmount: 'Enter amount',
        generating: 'Generating...',
        generateCode: 'Generate Code',
        readOnly: 'Read-only mode:',
        needGenerate: 'You can view donation data, but need',
        toCreate: 'to create codes.',
        error: 'Error:',
        success: 'Success:',
        codeGenerated: 'Code Generated Successfully!',
        copy: 'Copy',
        instructions: 'Instructions',
        instructionText: 'Share this code with the player. They can redeem it in-game using the donate system.',
        playersWithCoins: 'Players with Coins',
        totalCoins: 'Total Coins',
        averageCoins: 'Average Coins',
        generatedCodes: 'Generated Codes',
        totalCodeValue: 'Total Code Value',
        topCoinHolder: 'Top Coin Holder',
        na: 'N/A',
        coinBalances: 'Coin Balances',
        top10: 'Top 10',
        top50: 'Top 50',
        top100: 'Top 100',
        searchCoinBalances: 'Search by citizenid or player name',
        loadingCoinBalances: 'Loading coin balances...',
        noCoinBalances: 'No coin balance records found',
        playerName: 'Player Name',
        citizenId: 'Citizen ID',
        coins: 'Coins',
        manage: 'Manage',
        unknown: 'Unknown',
        manualCoinManagement: 'Manual Coin Management',
        amountShort: 'Amount',
        add: 'Add',
        remove: 'Remove',
        set: 'Set',
        activeCodes: 'Active Codes',
        loadingActiveCodes: 'Loading active codes...',
        noActiveCodes: 'No active codes found',
        actions: 'Actions',
        deleting: 'Deleting...',
        delete: 'Delete',
        trackingTitle: 'Donation Tracking',
        trackingSubtitle: 'How much players got, used, and moved during the period.',
        refreshTracking: 'Refresh Tracking',
        loadingTracking: 'Loading donation tracking...',
        noTracking: 'No donation activity found in this period',
        last72h: 'Last 72 Hours',
        lastWeek: 'Last 7 Days',
        allTime: 'All Time',
        graphTitle: 'Top Players — Coin Activity',
        dailyActivity: 'Daily Activity',
        got: 'Got',
        used: 'Used',
        moved: 'Moved',
        net: 'Net',
        operations: 'Operations',
        activePlayers: 'Active Players',
        lastAction: 'Last Action'
      };

  const failedOverviewMessage = t.failedOverview;
  const failedCodesMessage = t.failedCodes;

  const text = (template, params = {}) => template.replace(/\{(\w+)\}/g, (_, key) => params[key] ?? `{${key}}`);

  const fetchOverview = useCallback(async (viewOverride = 'top10', searchOverride = '') => {
    if (!canViewOverview) {
      setLoadingOverview(false);
      return;
    }

    try {
      setLoadingOverview(true);
      const selectedView = viewOverride;
      const selectedSearch = searchOverride;
      const params = new URLSearchParams();
      params.set('view', selectedView);
      if (selectedSearch && selectedSearch.trim()) {
        params.set('query', selectedSearch.trim());
      }

      const response = await apiFetch(`/api/donate/overview?${params.toString()}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || failedOverviewMessage);
      }

      const data = await response.json();
      setOverview({
        summary: data.summary || defaultDonateSummary,
        coins: data.coins || []
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingOverview(false);
    }
  }, [apiFetch, canViewOverview, failedOverviewMessage]);

  const fetchActiveCodes = useCallback(async () => {
    if (!canManageCodes) {
      setLoadingCodes(false);
      return;
    }

    try {
      setLoadingCodes(true);
      const response = await apiFetch('/api/donate/codes');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || failedCodesMessage);
      }

      const data = await response.json();
      setCodes(data.codes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingCodes(false);
    }
  }, [apiFetch, canManageCodes, failedCodesMessage]);

  const fetchTracking = useCallback(async (overrideWindow) => {
    if (!canViewOverview) {
      setLoadingTracking(false);
      return;
    }

    try {
      setLoadingTracking(true);
      const win = overrideWindow !== undefined ? overrideWindow : trackingWindow;
      const trackingUrl = win === 'all'
        ? '/api/donate/tracking?hours=all'
        : `/api/donate/tracking?hours=${win}`;
      const response = await apiFetch(trackingUrl);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch donate tracking');
      }

      const data = await response.json();
      setTracking({
        windowHours: Number(data.windowHours) || 72,
        totals: {
          got: Number(data?.totals?.got) || 0,
          used: Number(data?.totals?.used) || 0,
          moved: Number(data?.totals?.moved) || 0,
          net: Number(data?.totals?.net) || 0,
          operations: Number(data?.totals?.operations) || 0,
          activeCitizens: Number(data?.totals?.activeCitizens) || 0
        },
        players: Array.isArray(data.players) ? data.players : [],
        events: Array.isArray(data.events) ? data.events : [],
        dailyData: Array.isArray(data.dailyData) ? data.dailyData : []
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingTracking(false);
    }
  }, [apiFetch, canViewOverview, trackingWindow]);

  const changeTrackingWindow = (win) => {
    setTrackingWindow(win);
    fetchTracking(win);
  };

  useEffect(() => {
    if (canViewOverview) {
      fetchOverview(coinView, search);
      fetchTracking();
    } else {
      setLoadingOverview(false);
      setLoadingTracking(false);
    }

    if (canManageCodes) {
      fetchActiveCodes();
    } else {
      setLoadingCodes(false);
    }
  }, [fetchOverview, fetchActiveCodes, fetchTracking, canViewOverview, canManageCodes, coinView, search]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoadingGenerate(true);
    setError(null);
    setGeneratedCode(null);

    try {
      const response = await apiFetch('/api/donate/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: parseInt(amount) })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedGenerate);
      }

      const data = await response.json();
      setGeneratedCode(data);
      setAmount('');
      setSuccessMessage(t.generatedSuccess);
      fetchOverview(coinView, search);
      if (canManageCodes) {
        fetchActiveCodes();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm(t.confirmDelete)) {
      return;
    }

    try {
      setDeletingCodeId(codeId);
      setError(null);
      setSuccessMessage(null);

      const response = await apiFetch(`/api/donate/codes/${codeId}`, { method: 'DELETE' });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t.failedDelete);
      }

      setSuccessMessage(t.deletedSuccess);
      fetchActiveCodes();
      fetchOverview(coinView, search);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingCodeId(null);
    }
  };

  const updateCoinValueInput = (citizenid, value) => {
    setCoinInputValues((prev) => ({ ...prev, [citizenid]: value }));
  };

  const handleCoinUpdate = async (citizenid, action, customAmount) => {
    const amountValue = customAmount ?? coinInputValues[citizenid];
    const parsedAmount = Number(amountValue);

    if (!citizenid) {
      setError(t.citizenRequired);
      return;
    }

    if (Number.isNaN(parsedAmount) || parsedAmount < 0 || (action !== 'set' && parsedAmount <= 0)) {
      setError(t.validAmount);
      return;
    }

    try {
      setUpdatingCitizenId(citizenid);
      setError(null);
      setSuccessMessage(null);

      const response = await apiFetch(`/api/donate/coins/${encodeURIComponent(citizenid)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, amount: parsedAmount })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || t.failedUpdateCoins);
      }

      setSuccessMessage(text(t.coinsUpdated, { action, id: citizenid }));
      if (canViewOverview) {
        fetchOverview(coinView, search);
      }
      setCoinInputValues((prev) => ({ ...prev, [citizenid]: '' }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdatingCitizenId(null);
    }
  };

  const handleManualCoinUpdate = (action) => {
    handleCoinUpdate(manualCitizenId.trim(), action, manualAmount);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedCode.code);
    alert(t.copied);
  };

  const filteredCoins = overview.coins;

  const handleCoinViewChange = (nextView) => {
    setCoinView(nextView);
    fetchOverview(nextView, search);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const parseUiDate = (input) => {
    if (input === null || input === undefined || input === '') {
      return null;
    }

    if (typeof input === 'number' && Number.isFinite(input)) {
      const date = new Date(input);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const raw = String(input).trim();
    if (!raw) {
      return null;
    }

    if (/^\d+$/.test(raw)) {
      const numericDate = new Date(Number(raw));
      if (!Number.isNaN(numericDate.getTime())) {
        return numericDate;
      }
    }

    const parsed = raw.includes('T') ? new Date(raw) : new Date(`${raw}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatUiDate = (input) => {
    const parsed = parseUiDate(input);
    if (!parsed) {
      return String(input || '');
    }

    return formatEuDate(parsed);
  };

  const formatUiDateTime24 = (input) => {
    const parsed = parseUiDate(input);
    if (!parsed) {
      return String(input || '');
    }

    return formatEuDateTime(parsed);
  };

  if (!canViewOverview) {
    return (
      <div className="donate-code-generator">
        <div className="alert error">
          <strong>{t.accessDenied}</strong> {t.needDonateView} <code>donate.view</code> {t.toViewPage}
        </div>
      </div>
    );
  }

  return (
    <div className="donate-code-generator">
      <div className="donate-header">
        <h2>{t.title}</h2>
        <p>{t.subtitle}</p>
      </div>

      <div className="donate-actions">
        <button onClick={() => fetchOverview(coinView, search)} className="refresh-overview-button" disabled={loadingOverview}>
          {loadingOverview ? t.refreshing : `${t.refreshOverview}`}
        </button>
        <button onClick={fetchTracking} className="refresh-overview-button" disabled={loadingTracking}>
          {loadingTracking ? t.refreshing : `${t.refreshTracking}`}
        </button>
        {canManageCodes && (
          <button onClick={fetchActiveCodes} className="refresh-overview-button" disabled={loadingCodes}>
            {loadingCodes ? t.refreshingCodes : `${t.refreshCodes}`}
          </button>
        )}
      </div>

      {canGenerateCodes && (
      <form onSubmit={handleGenerate} className="generate-form">
        <div className="form-group">
          <label>{t.amount}</label>
          <input
            type="number"
            placeholder={t.enterAmount}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            min="1"
            step="1"
            required
          />
        </div>

        <button type="submit" className="generate-button" disabled={loadingGenerate}>
          {loadingGenerate ? t.generating : `${t.generateCode}`}
        </button>
      </form>
      )}

      {!canGenerateCodes && (
        <div className="alert success">
          <strong>{t.readOnly}</strong> {t.needGenerate} <code>codes.generate</code> {t.toCreate}
        </div>
      )}

      {error && (
        <div className="alert error">
          <strong>{t.error}</strong> {error}
        </div>
      )}

      {successMessage && (
        <div className="alert success">
          <strong>{t.success}</strong> {successMessage}
        </div>
      )}

      {generatedCode && (
        <div className="result-card">
          <h3>{t.codeGenerated}</h3>
          
          <div className="code-display">
            <div className="code-value">{generatedCode.code}</div>
            <button onClick={copyToClipboard} className="copy-button">
              {t.copy}
            </button>
          </div>

          <div className="code-info">
            <div className="info-item">
              <span className="info-label">Amount:</span>
              <span className="info-value">
                ${new Intl.NumberFormat('en-US').format(generatedCode.amount)}
              </span>
            </div>
          </div>

          <div className="instructions">
            <h4>{t.instructions}</h4>
            <p>{t.instructionText}</p>
          </div>
        </div>
      )}

      <div className="donate-stats-grid">
        <div className="donate-stat-card">
          <span className="donate-stat-label">{t.playersWithCoins}</span>
          <span className="donate-stat-value">{formatNumber(overview.summary.totalPlayersWithCoins)}</span>
        </div>
        <div className="donate-stat-card">
          <span className="donate-stat-label">{t.totalCoins}</span>
          <span className="donate-stat-value">{formatNumber(overview.summary.totalCoins)}</span>
        </div>
        <div className="donate-stat-card">
          <span className="donate-stat-label">{t.averageCoins}</span>
          <span className="donate-stat-value">{formatNumber(overview.summary.averageCoins)}</span>
        </div>
        <div className="donate-stat-card">
          <span className="donate-stat-label">{t.generatedCodes}</span>
          <span className="donate-stat-value">{formatNumber(overview.summary.totalCodes)}</span>
        </div>
        <div className="donate-stat-card">
          <span className="donate-stat-label">{t.totalCodeValue}</span>
          <span className="donate-stat-value">{formatNumber(overview.summary.totalCodeValue)}</span>
        </div>
        <div className="donate-stat-card">
          <span className="donate-stat-label">{t.topCoinHolder}</span>
          <span className="donate-stat-value donate-top-holder">
            {overview.summary.topCoinHolder
              ? `${overview.summary.topCoinHolder.playerName} (${formatNumber(overview.summary.topCoinHolder.credit)})`
              : t.na}
          </span>
        </div>
      </div>

      <div className="coin-balances-section">
        <div className="coin-balances-header">
          <h3>{t.trackingTitle}</h3>
          <div className="tracking-window-picker">
            <button
              className={`window-btn${trackingWindow === '72' ? ' active' : ''}`}
              onClick={() => changeTrackingWindow('72')}
              disabled={loadingTracking}
            >{t.last72h}</button>
            <button
              className={`window-btn${trackingWindow === '168' ? ' active' : ''}`}
              onClick={() => changeTrackingWindow('168')}
              disabled={loadingTracking}
            >{t.lastWeek}</button>
            <button
              className={`window-btn${trackingWindow === 'all' ? ' active' : ''}`}
              onClick={() => changeTrackingWindow('all')}
              disabled={loadingTracking}
            >{t.allTime}</button>
          </div>
        </div>
        <p className="tracking-subtitle">{t.trackingSubtitle}</p>

        <div className="donate-stats-grid tracking-grid">
          <div className="donate-stat-card">
            <span className="donate-stat-label">{t.got}</span>
            <span className="donate-stat-value">{formatNumber(tracking.totals.got)}</span>
          </div>
          <div className="donate-stat-card">
            <span className="donate-stat-label">{t.used}</span>
            <span className="donate-stat-value">{formatNumber(tracking.totals.used)}</span>
          </div>
          <div className="donate-stat-card">
            <span className="donate-stat-label">{t.moved}</span>
            <span className="donate-stat-value">{formatNumber(tracking.totals.moved)}</span>
          </div>
          <div className="donate-stat-card">
            <span className="donate-stat-label">{t.net}</span>
            <span className={`donate-stat-value ${tracking.totals.net >= 0 ? 'tracking-positive' : 'tracking-negative'}`}>
              {tracking.totals.net >= 0 ? '+' : ''}{formatNumber(tracking.totals.net)}
            </span>
          </div>
          <div className="donate-stat-card">
            <span className="donate-stat-label">{t.operations}</span>
            <span className="donate-stat-value">{formatNumber(tracking.totals.operations)}</span>
          </div>
          <div className="donate-stat-card">
            <span className="donate-stat-label">{t.activePlayers}</span>
            <span className="donate-stat-value">{formatNumber(tracking.totals.activeCitizens)}</span>
          </div>
        </div>

        {!loadingTracking && tracking.players.length > 0 && (() => {
          const top10 = tracking.players.slice(0, 10);
          const maxVal = Math.max(...top10.map((p) => Math.max(p.got, p.used)), 1);
          return (
            <div className="tracking-chart-wrap">
              <h4 className="chart-section-title">{t.graphTitle}</h4>
              <div className="player-bar-chart">
                {top10.map((player) => (
                  <div key={player.citizenid} className="player-chart-row">
                    <div className="player-chart-name">{player.playerName || player.citizenid}</div>
                    <div className="player-chart-bars">
                      <div className="chart-bar-line">
                        <span className="bar-type-label got">+</span>
                        <div className="bar-track">
                          <div className="bar-fill got" style={{ width: `${(player.got / maxVal) * 100}%` }} />
                        </div>
                        <span className="bar-val got-label">+{formatNumber(player.got)}</span>
                      </div>
                      <div className="chart-bar-line">
                        <span className="bar-type-label used">-</span>
                        <div className="bar-track">
                          <div className="bar-fill used" style={{ width: `${(player.used / maxVal) * 100}%` }} />
                        </div>
                        <span className="bar-val used-label">-{formatNumber(player.used)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {!loadingTracking && tracking.dailyData.length > 0 && (() => {
          const days = tracking.dailyData.slice(-21);
          const maxVal = Math.max(...days.map((d) => Math.max(d.got, d.used)), 1);
          const chartWidth = 1000;
          const chartHeight = 260;
          const padding = { top: 20, right: 18, bottom: 28, left: 38 };
          const innerWidth = chartWidth - padding.left - padding.right;
          const innerHeight = chartHeight - padding.top - padding.bottom;
          const stepX = days.length > 1 ? innerWidth / (days.length - 1) : 0;
          const pointX = (index) => padding.left + (index * stepX);
          const pointY = (value) => padding.top + (innerHeight - ((value / maxVal) * innerHeight));
          const baselineY = chartHeight - padding.bottom;

          const gotPoints = days
            .map((dayData, index) => `${pointX(index)},${pointY(dayData.got)}`)
            .join(' ');
          const usedPoints = days
            .map((dayData, index) => `${pointX(index)},${pointY(dayData.used)}`)
            .join(' ');

          const firstX = pointX(0);
          const lastX = pointX(days.length - 1);
          const gotAreaPath = `M ${days.map((dayData, index) => `${pointX(index)} ${pointY(dayData.got)}`).join(' L ')} L ${lastX} ${baselineY} L ${firstX} ${baselineY} Z`;
          const usedAreaPath = `M ${days.map((dayData, index) => `${pointX(index)} ${pointY(dayData.used)}`).join(' L ')} L ${lastX} ${baselineY} L ${firstX} ${baselineY} Z`;

          const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const value = Math.round(maxVal * ratio);
            return {
              ratio,
              value,
              y: padding.top + (innerHeight - (ratio * innerHeight))
            };
          });

          const labelStep = Math.max(1, Math.floor(days.length / 6));

          return (
            <div className="tracking-chart-wrap">
              <h4 className="chart-section-title">{t.dailyActivity}</h4>
              <div className="daily-line-chart">
                <svg className="line-chart-svg" viewBox={`0 0 ${chartWidth} ${chartHeight}`} role="img" aria-label={`${t.dailyActivity} line chart`}>
                  <defs>
                    <linearGradient id="gotAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--success-color)" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="var(--success-color)" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="usedAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--danger-color)" stopOpacity="0.2" />
                      <stop offset="100%" stopColor="var(--danger-color)" stopOpacity="0" />
                    </linearGradient>
                  </defs>

                  {yTicks.map((tick) => (
                    <g key={tick.ratio}>
                      <line
                        x1={padding.left}
                        y1={tick.y}
                        x2={chartWidth - padding.right}
                        y2={tick.y}
                        className="line-grid"
                      />
                      <text x={padding.left - 8} y={tick.y + 4} className="line-axis-label" textAnchor="end">
                        {formatNumber(tick.value)}
                      </text>
                    </g>
                  ))}

                  <line
                    x1={padding.left}
                    y1={baselineY}
                    x2={chartWidth - padding.right}
                    y2={baselineY}
                    className="line-axis"
                  />

                  <path d={gotAreaPath} className="line-area got" fill="url(#gotAreaGradient)" />
                  <path d={usedAreaPath} className="line-area used" fill="url(#usedAreaGradient)" />
                  <polyline points={gotPoints} className="line-path got" fill="none" />
                  <polyline points={usedPoints} className="line-path used" fill="none" />

                  {days.map((dayData, index) => {
                    const x = pointX(index);
                    const gotY = pointY(dayData.got);
                    const usedY = pointY(dayData.used);
                    const rawDay = String(dayData.day || '');
                    const label = formatUiDate(rawDay);
                    const tooltipDate = formatUiDateTime24(rawDay);
                    const showLabel = (index % labelStep === 0) || index === days.length - 1;

                    return (
                      <g key={dayData.day}>
                        <circle cx={x} cy={gotY} r="3.5" className="line-point got" />
                        <circle cx={x} cy={usedY} r="3.5" className="line-point used" />
                        {showLabel && (
                          <text x={x} y={chartHeight - 8} className="line-x-label" textAnchor="middle">
                            {label}
                          </text>
                        )}
                        <title>{`${tooltipDate}\n+${formatNumber(dayData.got)} ${t.got}\n-${formatNumber(dayData.used)} ${t.used}`}</title>
                      </g>
                    );
                  })}
                </svg>
              </div>
              <div className="chart-legend">
                <span className="legend-dot got">●</span> {t.got}
                &nbsp;&nbsp;
                <span className="legend-dot used">●</span> {t.used}
              </div>
            </div>
          );
        })()}

        {loadingTracking ? (
          <div className="balances-placeholder">{t.loadingTracking}</div>
        ) : tracking.players.length === 0 ? (
          <div className="balances-placeholder">{t.noTracking}</div>
        ) : (
          <div className="coin-table-wrap">
            <table className="coin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t.playerName}</th>
                  <th>{t.citizenId}</th>
                  <th>{t.coins}</th>
                  <th>{t.got}</th>
                  <th>{t.used}</th>
                  <th>{t.moved}</th>
                  <th>{t.net}</th>
                  <th>{t.operations}</th>
                  <th>{t.lastAction}</th>
                </tr>
              </thead>
              <tbody>
                {tracking.players.map((player, index) => (
                  <tr key={`${player.citizenid}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{player.playerName || t.unknown}</td>
                    <td>{player.citizenid}</td>
                    <td className="coin-value">{formatNumber(player.currentCredit)}</td>
                    <td className="tracking-positive">+{formatNumber(player.got)}</td>
                    <td className="tracking-negative">-{formatNumber(player.used)}</td>
                    <td>{formatNumber(player.moved)}</td>
                    <td className={Number(player.net) >= 0 ? 'tracking-positive' : 'tracking-negative'}>
                      {Number(player.net) >= 0 ? '+' : ''}{formatNumber(player.net)}
                    </td>
                    <td>{formatNumber(player.operations)}</td>
                    <td>{player.lastActionType || t.na}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canManageCoins && (
      <div className="coin-balances-section">
        <div className="coin-balances-header">
          <h3>{t.coinBalances} (ak4y_donatesystem)</h3>
          <div className="coin-view-toggle">
            <button
              className={coinView === 'top10' ? 'active' : ''}
              onClick={() => handleCoinViewChange('top10')}
            >
              {t.top10}
            </button>
            <button
              className={coinView === 'top50' ? 'active' : ''}
              onClick={() => handleCoinViewChange('top50')}
            >
              {t.top50}
            </button>
            <button
              className={coinView === 'top100' ? 'active' : ''}
              onClick={() => handleCoinViewChange('top100')}
            >
              {t.top100}
            </button>
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              const nextSearch = e.target.value;
              setSearch(nextSearch);
              fetchOverview(coinView, nextSearch);
            }}
            className="coin-search"
            placeholder={t.searchCoinBalances}
          />
        </div>

        {loadingOverview ? (
          <div className="balances-placeholder">{t.loadingCoinBalances}</div>
        ) : filteredCoins.length === 0 ? (
          <div className="balances-placeholder">{t.noCoinBalances}</div>
        ) : (
          <div className="coin-table-wrap">
            <table className="coin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t.playerName}</th>
                  <th>{t.citizenId}</th>
                  <th>{t.coins}</th>
                  <th>{t.manage}</th>
                </tr>
              </thead>
              <tbody>
                {filteredCoins.map((row, index) => (
                  <tr key={`${row.citizenid}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{row.playerName || t.unknown}</td>
                    <td>{row.citizenid}</td>
                    <td className="coin-value">{formatNumber(row.credit)}</td>
                    <td>
                      <div className="coin-manage-row">
                        <input
                          type="number"
                          min="0"
                          className="coin-manage-input"
                          value={coinInputValues[row.citizenid] || ''}
                          onChange={(e) => updateCoinValueInput(row.citizenid, e.target.value)}
                          placeholder={t.amountShort}
                        />
                        <button
                          className="coin-action-btn add"
                          disabled={updatingCitizenId === row.citizenid}
                          onClick={() => handleCoinUpdate(row.citizenid, 'add')}
                        >
                          +
                        </button>
                        <button
                          className="coin-action-btn remove"
                          disabled={updatingCitizenId === row.citizenid}
                          onClick={() => handleCoinUpdate(row.citizenid, 'remove')}
                        >
                          -
                        </button>
                        <button
                          className="coin-action-btn set"
                          disabled={updatingCitizenId === row.citizenid}
                          onClick={() => handleCoinUpdate(row.citizenid, 'set')}
                        >
                          {t.set}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {canManageCoins && (
      <div className="coin-balances-section">
        <div className="coin-balances-header">
          <h3>{t.manualCoinManagement}</h3>
        </div>
        <div className="manual-coin-form">
          <input
            type="text"
            value={manualCitizenId}
            onChange={(e) => setManualCitizenId(e.target.value)}
            className="coin-search"
            placeholder="Citizen ID"
          />
          <input
            type="number"
            min="0"
            value={manualAmount}
            onChange={(e) => setManualAmount(e.target.value)}
            className="coin-search"
            placeholder={t.amountShort}
          />
          <button className="coin-action-btn add" onClick={() => handleManualCoinUpdate('add')}>{t.add}</button>
          <button className="coin-action-btn remove" onClick={() => handleManualCoinUpdate('remove')}>{t.remove}</button>
          <button className="coin-action-btn set" onClick={() => handleManualCoinUpdate('set')}>{t.set}</button>
        </div>
      </div>
      )}

      {canManageCodes && (
      <div className="coin-balances-section">
        <div className="coin-balances-header">
          <h3>{t.activeCodes}</h3>
        </div>
        {loadingCodes ? (
          <div className="balances-placeholder">{t.loadingActiveCodes}</div>
        ) : codes.length === 0 ? (
          <div className="balances-placeholder">{t.noActiveCodes}</div>
        ) : (
          <div className="coin-table-wrap">
            <table className="coin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Code</th>
                  <th>Credit</th>
                  <th>{t.actions}</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.id}>
                    <td>{code.id}</td>
                    <td>{code.code}</td>
                    <td className="coin-value">{formatNumber(code.credit)}</td>
                    <td>
                      <button
                        className="coin-action-btn remove"
                        disabled={deletingCodeId === code.id}
                        onClick={() => handleDeleteCode(code.id)}
                      >
                        {deletingCodeId === code.id ? t.deleting : t.delete}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

    </div>
  );
}

export default DonateCodeGenerator;



